// agora.js
'use strict';

import loadScriptTag from '../resource/loadScriptTag.js';
import env from '../resource/env.js';
import { player as playerDebug } from '../resource/debug.js';
import {
  getAgoraInstance,
  getOrCreateAgoraInstance,
  leaveAgoraChannel,
  removeAllAgoraInstances,
  setAgoraInstanceStatus,
  waitAgoraChannelLeave,
} from '../resource/agoraClientPool.js';

const log = playerDebug.extend('log:agora');
const errorLog = playerDebug.extend('error:agora');

export { setAgoraInstanceStatus };

// Agora SDK
let loadAgoraScriptPromise;

export const loadAgoraScript = async () => {
  if (loadAgoraScriptPromise) {
    await loadAgoraScriptPromise;
    return;
  }

  loadAgoraScriptPromise = loadScriptTag({
    id: `agora-rtc-${env.AGORA_RTC_VERSION}`,
    src: `https://download.agora.io/sdk/release/AgoraRTC_N-${env.AGORA_RTC_VERSION}.js`,
  });

  log('start load agora client');
  await loadAgoraScriptPromise;
  log('load agora client done');

  if (window.AgoraRTC) {
    // AgoraRTC default enable debug log
    // 0: DEBUG. Output all API logs.
    // 1: INFO. Output logs of the INFO, WARNING and ERROR level.
    // 2: WARNING. Output logs of the WARNING and ERROR level.
    // 3: ERROR. Output logs of the ERROR level.
    // 4: NONE. Do not output any log.
    let debugLevel = 4;
    if (new URLSearchParams(window.location.search).has('debug-agora')) {
      debugLevel = +new URLSearchParams(window.location.search).get(
        'debug-agora'
      );
    }
    window.AgoraRTC.setLogLevel(debugLevel);
  }
};

export const initializeAgoraClient = ({ codec }) => {
  log('execute initialize agora client', { codec });
  // create client reference: https://api-ref.agora.io/en/video-sdk/web/4.x/interfaces/clientconfig.html
  return window.AgoraRTC.createClient({
    mode: 'live',
    role: 'audience',
    clientRoleOptions: {
      // reference: https://doc.shengwang.cn/doc/rtc/javascript/basic-features/ls-quick-start
      // reference: https://api-ref.agora.io/en/video-sdk/web/4.x/enums/audiencelatencyleveltype.html
      // 1: Low latency.
      // 2: (Default) Ultra low latency.
      level: 1,
    },
    codec,
  });
};

// https://api-ref.agora.io/en/video-sdk/web/4.x/globals.html#connectionstate
export const AGORA_CLIENT_CONNECTION_STATE = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
};

export const getAgoraConnectionState = ({ channelId }) => {
  return getAgoraInstance({ channelId })?.client?.connectionState;
};

export const bindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.on(eventName, listener);
  });
};

export const unbindAgoraRTCEventListeners = ({ listeners }) => {
  listeners.forEach(({ eventName, listener }) => {
    window.AgoraRTC?.off(eventName, listener);
  });
};

export const cleanupAgoraRTCListeners = () => {
  window.AgoraRTC?.removeAllListeners();
};

// Agora Instance Operations (listeners bind/unbind & renew token & leave)
export const bindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.on(eventName, listener);
  });
};

export const unbindAgoraClientEventListeners = ({ channelId, listeners }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  listeners.forEach(({ eventName, listener }) => {
    agoraClient?.off(eventName, listener);
  });
};

export const renewToken = async ({ channelId, token }) => {
  const agoraClient = getAgoraInstance({ channelId })?.client;
  if (!agoraClient) {
    errorLog('fail to renew token: agora client not found', {
      channelId,
      token,
    });
    return;
  }
  try {
    await agoraClient.renewToken(token);
    log('successfully renewed token', { channelId, token });
  } catch (error) {
    errorLog('fail to renew token', { channelId, token, error });
    throw error;
  }
};

/**
 * Agora Client Promise Chain Flow
 *
 * joinChannelWithoutSubscribingPromise (Preloader 觸發，Player 複用)
 * │
 * ├─ 1. loadScriptPromise           ← loadAgoraScript()
 * │
 * ├─ 2. createClientPromise          ← initializeAgoraClient()
 * │     └─ bindInternalClientListeners()
 * │
 * ├─ 3. joinChannelPromise           ← client.join(appId, channelId, token, sessionId)
 * │
 * └─ 4. waitForPublishingPromise     ← wait video + audio user-published (10s timeout)
 *       └─ resolve()
 *
 *
 * joinChannelWithSubscribingPromise (Player 觸發)
 * │
 * ├─ reuse joinChannelWithoutSubscribingPromise
 * │  (若 Preloader 已建立 → 直接 await，不重建)
 * │
 * └─ 5. subscribeTrackPromise        ← client.subscribe(remoteUser, 'video')
 *                                       client.subscribe(remoteUser, 'audio')
 *       └─ return { client, track: { videoTrack, audioTrack } }
 *
 *
 * AgoraInstance Property Reset Levels
 * (僅重置 agoraInstance 上的 cached promise，不會觸發 SDK 的 join/leave/subscribe)
 *
 * resetSubscription ⊂ resetPublishing ⊂ resetConnection
 *
 * resetSubscription: subscribeTrackPromise, joinChannelWithSubscribingPromise
 * resetPublishing:   + waitForPublishingPromise, joinChannelWithoutSubscribingPromise, publishedResolve
 * resetConnection:   + joinChannelPromise, publishedTracks
 */

const WAIT_FOR_PUBLISHING_TIMEOUT_MS = 10000;

// --- AgoraInstance Property Reset Helpers (no SDK operations) ---

const resetSubscription = agoraInstance => {
  agoraInstance.subscribeTrackPromise = null;
  agoraInstance.joinChannelWithSubscribingPromise = null;
};

const resetPublishing = agoraInstance => {
  resetSubscription(agoraInstance);
  agoraInstance.waitForPublishingPromise = null;
  agoraInstance.joinChannelWithoutSubscribingPromise = null;
  agoraInstance.publishedResolve = null;
};

const resetConnection = agoraInstance => {
  resetPublishing(agoraInstance);
  agoraInstance.joinChannelPromise = null;
  agoraInstance.publishedTracks = {};
};

// --- Internal Helpers ---

const collectTracksFromRemoteUsers = client => {
  let videoTrack = null;
  let audioTrack = null;
  for (const remoteUser of client?.remoteUsers || []) {
    if (remoteUser?.videoTrack) videoTrack = remoteUser.videoTrack;
    if (remoteUser?.audioTrack) audioTrack = remoteUser.audioTrack;
  }
  return { videoTrack, audioTrack };
};

const bindInternalClientListeners = (client, agoraInstance, channelId) => {
  client.on('user-published', (remoteUser, mediaType) => {
    agoraInstance.publishedTracks[mediaType] = remoteUser;
    log('user-published', { channelId, mediaType, uid: remoteUser.uid });
    if (
      agoraInstance.publishedTracks.video &&
      agoraInstance.publishedTracks.audio &&
      agoraInstance.publishedResolve
    ) {
      agoraInstance.publishedResolve();
    }
  });

  client.on('user-unpublished', (remoteUser, mediaType) => {
    log('user-unpublished', { channelId, mediaType });
    delete agoraInstance.publishedTracks[mediaType];
    resetPublishing(agoraInstance);
  });

  client.on('connection-state-change', (curState, prevState, reason) => {
    log('connection-state-change', { channelId, curState, prevState, reason });
    if (curState === 'DISCONNECTED') {
      resetConnection(agoraInstance);
    }
  });
};

// --- Core Flows ---

const joinChannelWithoutSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
}) => {
  const agoraInstance = getOrCreateAgoraInstance({ channelId });

  // load script
  if (!agoraInstance.loadScriptPromise) {
    agoraInstance.loadScriptPromise = loadAgoraScript();
  }
  await agoraInstance.loadScriptPromise;

  // create client & bind internal listeners
  if (!agoraInstance.createClientPromise) {
    const client = initializeAgoraClient({ codec: 'h264' });
    agoraInstance.client = client;
    agoraInstance.createClientPromise = Promise.resolve(client);
    bindInternalClientListeners(client, agoraInstance, channelId);
  }
  const client = await agoraInstance.createClientPromise;

  // join channel — channelId is passed as the Agora SDK "channel" parameter
  if (!agoraInstance.joinChannelPromise) {
    await waitAgoraChannelLeave({ channelId });
    if (!appId || !token) {
      throw new Error(
        `joinChannel failed: missing required params (appId: ${!!appId}, token: ${!!token})`
      );
    }
    log('joinChannelWithoutSubscribing: joining', { channelId });
    agoraInstance.joinChannelPromise = client.join(
      appId,
      channelId,
      token,
      sessionId
    );
  }
  try {
    await agoraInstance.joinChannelPromise;
  } catch (error) {
    errorLog('joinChannelWithoutSubscribingCore: join failed', {
      channelId,
      error,
    });
    resetConnection(agoraInstance);
    throw error;
  }
  log('joinChannelWithoutSubscribing: joined', { channelId });

  // wait for both video & audio published (with timeout)
  if (!agoraInstance.waitForPublishingPromise) {
    const { promise, resolve } = Promise.withResolvers();
    agoraInstance.publishedResolve = resolve;
    if (
      agoraInstance.publishedTracks.video &&
      agoraInstance.publishedTracks.audio
    ) {
      resolve();
    }
    agoraInstance.waitForPublishingPromise = Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('waitForPublishing timeout')),
          WAIT_FOR_PUBLISHING_TIMEOUT_MS
        )
      ),
    ]);
  }

  return { client };
};

const joinChannelWithSubscribingCore = async ({
  appId,
  channelId,
  token,
  sessionId,
}) => {
  const agoraInstance = getOrCreateAgoraInstance({ channelId });
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({ appId, channelId, token, sessionId });
  }
  const { client } = await agoraInstance.joinChannelWithoutSubscribingPromise;
  log('joinChannelWithSubscribing: joined', { channelId });

  await agoraInstance.waitForPublishingPromise;
  log('joinChannelWithSubscribing: published', { channelId });

  // subscribe & collect media tracks
  if (!agoraInstance.subscribeTrackPromise) {
    agoraInstance.subscribeTrackPromise = (async () => {
      const existingTracks = collectTracksFromRemoteUsers(client);
      if (existingTracks.videoTrack && existingTracks.audioTrack) {
        log('joinChannelWithSubscribing: already subscribed', {
          channelId,
          ...existingTracks,
        });
        return existingTracks;
      }
      try {
        log('joinChannelWithSubscribing: subscribing', { channelId });
        for (const [mediaType, remoteUser] of Object.entries(
          agoraInstance.publishedTracks
        )) {
          await client.subscribe(remoteUser, mediaType);
          log('joinChannelWithSubscribing: subscribed', {
            channelId,
            mediaType,
          });
        }
      } catch (error) {
        errorLog('joinChannelWithSubscribing: subscribe failed', {
          channelId,
          error,
        });
        agoraInstance.subscribeTrackPromise = null;
        throw error;
      }
      return collectTracksFromRemoteUsers(client);
    })();
  }
  const track = await agoraInstance.subscribeTrackPromise;
  return { client, track };
};

// --- Exported API ---

export const joinChannelWithoutSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
  isNearBy = true,
}) => {
  const agoraInstance = getOrCreateAgoraInstance({ channelId });
  setAgoraInstanceStatus({ channelId, isNearBy });
  if (!agoraInstance.joinChannelWithoutSubscribingPromise) {
    agoraInstance.joinChannelWithoutSubscribingPromise =
      joinChannelWithoutSubscribingCore({ appId, channelId, token, sessionId });
  }
  try {
    const { client } = await agoraInstance.joinChannelWithoutSubscribingPromise;
    return client;
  } catch (error) {
    errorLog('joinChannelWithoutSubscribing: failed', { channelId, error });
    resetPublishing(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

export const joinChannelWithSubscribing = async ({
  appId,
  channelId,
  token,
  sessionId,
  isActive = true,
}) => {
  const agoraInstance = getOrCreateAgoraInstance({ channelId });
  if (isActive) {
    setAgoraInstanceStatus({ channelId, isActive: true });
  }
  if (!agoraInstance.joinChannelWithSubscribingPromise) {
    agoraInstance.joinChannelWithSubscribingPromise =
      joinChannelWithSubscribingCore({ appId, channelId, token, sessionId });
  }
  try {
    const { client, track } =
      await agoraInstance.joinChannelWithSubscribingPromise;
    return { client, track };
  } catch (error) {
    errorLog('joinChannelWithSubscribing: failed', { channelId, error });
    resetConnection(agoraInstance);
    leaveAgoraChannel({ channelId });
    throw error;
  }
};

export const cleanupAllAgoraInstances = () => removeAllAgoraInstances();

export const unsubscribeJoinedChannel = async ({ channelId }) => {
  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance || !agoraInstance.client) {
    errorLog('unsubscribeJoinedChannel: agora client not found', { channelId });
    return;
  }
  log('unsubscribeJoinedChannel: start', { channelId });
  setAgoraInstanceStatus({ channelId, isActive: false });
  resetSubscription(agoraInstance);
  const remoteUsers = agoraInstance.client?.remoteUsers;
  if (!remoteUsers?.length) {
    errorLog('unsubscribeJoinedChannel: no remote users', { channelId });
    return;
  }
  try {
    for (const remoteUser of remoteUsers) {
      if (remoteUser?.videoTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'video');
      }
      if (remoteUser?.audioTrack) {
        await agoraInstance.client.unsubscribe(remoteUser, 'audio');
      }
    }
  } catch (error) {
    errorLog('unsubscribeJoinedChannel failed', { channelId, error });
  }
  log('unsubscribeJoinedChannel: done', { channelId });
};
